// Device detection (GROUND-M stage 0): WHICH player this is, decided once at
// boot before any terrain material compiles, because the detail tier is baked
// into the shader strings (terrain.js initDetailQuality).
//
//   class   classifyDevice(gpu, ua, touch) — pure, from the unmasked renderer
//           string + the user agent. software | quest | phone-hi | phone-lo |
//           desktop-igpu | desktop-dgpu. `pcvr` is not knowable at boot (the
//           same desktop GPU may or may not enter VR); the load report names it
//           from mode 'vr' on a desktop class.
//   tier    renderconfig.ground.tier_by_class[class]
//   probe   startGpuProbe: an off-screen texture-tap pass on its own context
//           at boot (a return visit with a stored result: deferGpuProbe,
//           after the first beam-in release), ms per 1024² draw (GPU timer queries; the median slope of
//           interleaved wall-clock batches without them). It may DEMOTE the class's tier by
//           one when slower than renderconfig.ground.probe.demote_ms[class];
//           it never promotes, a class without a threshold (the Quest: a
//           fixed, known target) is never demoted, and an UNTRUSTED time (too
//           few samples, a clamped clock, disagreeing samples, still running
//           when the tier is decided with no stored result of an earlier
//           load: probe_why) keeps the class tier (why 'unsure'). Safari masks the GPU ("Apple GPU"),
//           Brave too ("Brave"): the class reads the UA, only the probe can tell.
//   force   ?ground=0|1|2 in the page URL, or a pinned
//           renderconfig.detail_quality (low|mid|high), skips class + probe.
//   ?dev=<class> in the page URL (one of DEVICE_CLASSES) PINS the class
//           (urlClass; why_cls 'url'): everything that reads the class — the
//           ground tier + its probe demotion, GmTex pools, adaptres,
//           gensched's tunables block — takes it; the UA-driven modes (touch
//           controls, the landing's device cards) do not. Desktop
//           approximation of Quest / phone for tools/gpuprobe.mjs --dev and
//           the owner (APP_GENSURF §4a).
//
// The class, the probe time and the tier ride the telemetry + load reports
// (main.js) so the owner sets tier_by_class from real devices.

import * as THREE from 'three';

export const DEVICE_CLASSES = ['software', 'quest', 'phone-hi', 'phone-lo', 'desktop-igpu', 'desktop-dgpu'];

// the class detectDevice decided (null before it ran): gmtex.js pool sizes,
// adaptres.js (automatic resolution) read it
let _cls = null;
export function deviceClass() { return _cls; }
// THE WEAK TIER (all-res 2026-11-02, APP_SURFACE "Device classes" → the class /
// tier table): a device whose decided ground tier is BELOW its class's own tier
// (renderconfig ground.tier_by_class) — the GPU probe measured it slower than
// the class's demote_ms (why 'probe'), or a ?ground= / detail_quality pin
// forces it lower. Such a device takes the MILD look trades (WEAK_DRAW /
// WEAK_OFF); a device at its class tier draws as today. The Quest is never
// weak (never probed; its own rows). Measured capability, not the name alone.
let _weak = false;
export function deviceWeak() { return _weak; }

// Tier terms a class drops while it keeps its tier (owner ruling igpu-cuts
// 2026-10-24: the iGPU frame is fill-bound; it keeps tier 2 but draws no S2
// rough self-shadow / parallax). A CLIENT rule, never renderconfig (a bake-id
// term): groundmat.js ROUGH_ON reads it, so the variant never compiles there.
// quest (quest-draws 2026-11-01, owner-approved subtle pixel trade): a
// transparent DoubleSide material is TWO draws unless forceSinglePass (three:
// back faces, then front) — the station domes + the funicular car glass draw
// in ONE pass on the headset (stationzones.js _pulseMat, funicularcar.js).
// quest-merge (2026-11-01): cloud_sort = every cloud its own depth-sorted
// mesh; off on quest = ONE InstancedMesh per cloud material (sky.js
// buildClouds: 12 draws -> 3, the instances in slot order, renderOrder -1 =
// first of the transparents, as the farthest).
// quest-ground (2026-11-02): gm_relight = the ground's detail-normal relight
// (groundmat.js RELIGHT_ON): off on quest, its uMatB taps never compile there.
export const CLASS_OFF = { 'desktop-igpu': ['rough_on'], quest: ['glass_2pass', 'cloud_sort', 'gm_relight'] };
// THE TERMS A CLASS TURNS ON (quest-multiview 2026-11-02, APP_SURFACE_PERF "The XR path"):
//   xr_multiview  both eyes in ONE pass (OVR_multiview2) on a texture-array
//                 projection layer (xrmv.js; the vendored three patch) — the
//                 vertex / binning / draw work once for two eyes
//   xr_depth      the eye depth submitted with that layer (ignoreDepthValues
//                 false when the runtime takes it: positional reprojection);
//                 off = an own depth array, invalidated after the frame
// every other class never installs the controller: three's per-eye path
// (space warp, spacewarp.js: requested as an optional feature on the xr_multiview class; it changes a session only
// when the session grants 'space-warp' — no class term of its own)
export const CLASS_ON = { quest: ['xr_multiview', 'xr_depth'] };
export function classOn(term, cls = _cls) { return !!(CLASS_ON[cls] && CLASS_ON[cls].includes(term)); }
// the weak tier's milder terms off (THE WEAK TIER above; a class with its own
// CLASS_OFF row keeps that row): one-pass glass (half the glass fragments),
// one instanced mesh a cloud material (12 cloud draws -> 3) — the Quest's two
// subtlest trades; the ground relight stays (the Quest's gm_relight is not mild)
export const WEAK_OFF = ['glass_2pass', 'cloud_sort'];
// cls: the class (default the decided one; null = none yet -> nothing off);
// weak: the weak tier (default: the decided device's when cls is its class)
export function classOff(term, cls = _cls, weak = cls === _cls && _weak) {
  const row = CLASS_OFF[cls] || [];
  return row.includes(term) || (!!weak && cls !== 'quest' && WEAK_OFF.includes(term));
}

// THE DRAW RULE per class (quest-budget 2026-11-01, APP_PLAYER "Automatic
// resolution"): a headset frame is CPU-bound by draw SUBMISSION (session 870,
// Quest 3: ~600 draws both eyes = rnd_ms 21 at ~35 µs a draw, the tri count
// uncorrelated), so a class draws the SAME truth coarser, never less of it:
//   px        × the ground tree's split error in screen px (groundtree.js
//             _opt, morph.js D = err·K/px): fewer, coarser nodes of the
//             error-bounded quadtree, every one still error-bounded (L0 is
//             the truth; APP_GROUNDM)
//   bld_cull  × renderconfig buildings_cull_m: the building ring's residency
//             radius (buildings.js this.cull; the 1024 m GLB tiles beyond
//             are neither fetched nor drawn)
// quest-draws (2026-11-01, owner-approved subtle Quest-only pixel trades for a
// steady 72 Hz; px 1.25 -> 2 = 8 px, a coarser far ground; quest-ground below: 4):
//   decor_view × stationzones.js DECOR_VIEW_M: the station boards / clock /
//              poster container shows from closer (350 -> 140 m)
//   zoom_rt    × the head zoom window's render target (weapons.js monoRt
//              1024×640 -> 768×480: the same picture, fewer pixels)
//   zoom_every × the window's render cadence (every 2nd -> every 3rd frame;
//              rounded; monoDrawsNext keeps step)
//   runs       × viscull.js's P3 cell-run cap MAX_RUNS (4 -> 1: a ground node /
//              GLB tile / volume group is ONE draw instead of up to four
//              visible runs; a merged gap draws more triangles, never fewer:
//              the same pixels — the vertex saving goes, the draws go with it)
// quest-merge (2026-11-01, the same ruling; a DRAW is the unit, a merged unit
// draws more off-screen triangles, never fewer pixels):
//   tree_cell  tree sprite pack tiles a side of one DRAW cell (trees.js cellT,
//              rounded: quest 2 = 2048 m): one instanced mesh a species per
//              cell instead of per 1024 m tile — the same instances, a coarser
//              frustum cull (more off-screen instances vertex-processed)
//   tree_view  × the tree sprites' live view (trees.js treeViewM): the cap
//              radius the hand-off already moves in dense forest is held to
//              0.6 × view (1100 -> 660 m); the canopy volume is the forest
//              beyond, as it is past the cap today — fewer sprite chunks
//   water_chunk × sky.js WATER_CHUNK_M (waterCellM): 4096 -> 16384 m = ONE
//              river mesh + ONE sheet mesh (same triangles; the frustum cull
//              of the 8 cells goes)
//   lift_cell  × aerialway.js LIFT_CELL_M (liftCellM): 2048 -> 16384 m = ONE
//              chair-cable + ONE chair-structure mesh for every chair line
//   way_cell   × ways2.js's ribbon upload cell (wayCellM): the WAY1 tiles'
//              meshes of one (material, view class) merged per 1024 m cell
//              (2 × RIBBON_CHUNK); the per-class view rule measures from the
//              cell's centre (a far piece shows up to ~360 m sooner / later)
// quest-ground (2026-11-02, the same ruling; the headset is GPU-bound and the
// ground is 8.2 of its 13.6 ms, questeyes ab1): px 2 -> 4 (a 16 px split of
// Quest K = 9.6 eye px at the 0.6 viewport scale) and
//   stand      × morph.js LOD_M stand + standW (groundtree.js standOf):
//              L0 is forced within 32 m + 8 m a level (64 + 16 elsewhere);
//              past it the error rule picks the node, every one still
//              error-bounded. The standing ground stays L0 everywhere
//              (client/src/ground.js, game/ground.js read L0 only)
// A CLIENT rule, never renderconfig (a bake-id term); 1 / absent = the class
// draws as the desktop. adaptres.js drawK() scales px and bld_cull further
// from measured CPU time (the draw rung).
export const CLASS_DRAW = { quest: { px: 4, stand: 0.5, bld_cull: 0.82, decor_view: 0.4, zoom_rt: 0.75, zoom_every: 1.5, runs: 0.25,
  tree_view: 0.6, tree_cell: 2, water_chunk: 4, lift_cell: 8, way_cell: 2 } };
// THE WEAK TIER's milder draw (all-res 2026-11-02; a class with its own
// CLASS_DRAW row — the Quest — keeps it): a coarser ground split (px 1.5 = 6
// px, the Quest 4 = 16), a building ring 0.9 (2000 m, the Quest 0.82), station
// decor from 245 m (the Quest 140), tree sprites to 0.85 x the view (the Quest
// 0.6). L0 at the feet (stand), the zoom window and every merge term stay
// (the merges trade draws for vertex work: a GPU-weak device gains nothing).
export const WEAK_DRAW = { px: 1.5, bld_cull: 0.9, decor_view: 0.7, tree_view: 0.85 };
// term: 'px' | 'stand' | 'bld_cull' | 'decor_view' | 'zoom_rt' | 'zoom_every' | 'runs' | 'tree_view' | 'tree_cell' | 'water_chunk' | 'lift_cell' | 'way_cell';
// cls: the class (default the decided one), weak: the weak tier (default: the
// decided device's when cls is its class) -> the factor (1 = untouched)
export function classDraw(term, cls = _cls, weak = cls === _cls && _weak) {
  const row = CLASS_DRAW[cls] || (weak && cls !== 'quest' ? WEAK_DRAW : null);
  const v = row && row[term];
  return v > 0 ? v : 1;
}
// the head zoom window's render target (weapons.js monoRt): 1024×640 × zoom_rt,
// the same 1.6 aspect (the 1024×640 overlay / label space is independent of it)
export const ZOOM_RT = [1024, 640];
export const zoomRtSize = (cls = _cls) => ZOOM_RT.map((v) => Math.round(v * classDraw('zoom_rt', cls)));
// its VR render cadence: every 2nd frame × zoom_every, an integer >= 1
export const zoomEvery = (cls = _cls) => Math.max(1, Math.round(2 * classDraw('zoom_every', cls)));

// gpu: UNMASKED_RENDERER_WEBGL (or RENDERER); ua: navigator.userAgent;
// touch: a touch screen is present (iPadOS reports a desktop Safari UA)
// ?dev=<class> in a location.search string → the class, else null
export function urlClass(search) {
  const v = new URLSearchParams(search || '').get('dev');
  return DEVICE_CLASSES.includes(v) ? v : null;
}

export function classifyDevice(gpu, ua, touch) {
  const g = String(gpu || ''), u = String(ua || '');
  if (/swiftshader|llvmpipe|softpipe|software|basic render/i.test(g)) return 'software';
  if (/OculusBrowser|Quest|Pacific/i.test(u) || /oculus|quest/i.test(g)) return 'quest';
  const mobileUa = /Android|iPhone|iPad|iPod/i.test(u) || (touch && /Macintosh/i.test(u) && /Safari/i.test(u));
  const mobileGpu = /adreno|mali|powervr|xclipse|immortalis/i.test(g);
  if (mobileUa || mobileGpu) {
    const adreno = /adreno[^0-9]*(\d{3})/i.exec(g);
    const maliG = /mali-g(\d{2,3})/i.exec(g);
    const xclipse = /xclipse[^0-9]*(\d{3})/i.exec(g);
    const hi = (adreno && +adreno[1] >= 730)
      || /immortalis/i.test(g)
      || (maliG && +maliG[1] >= 710)
      || (xclipse && +xclipse[1] >= 920)
      || /apple/i.test(g) || (!mobileGpu && /iPhone|iPad|Macintosh/i.test(u));
    return hi ? 'phone-hi' : 'phone-lo';
  }
  const dgpu = /nvidia|geforce|quadro|\brtx\b|\bgtx\b/i.test(g)
    || /radeon[^,)]*\b(rx|pro|vii|r9|r7)\b/i.test(g)
    || /\barc(\(tm\))?\s+a\d{3}/i.test(g)
    || /apple m\d+ (pro|max|ultra)/i.test(g);
  return dgpu ? 'desktop-dgpu' : 'desktop-igpu';
}

// A fixed off-screen pass on the probe's OWN WebGL2 context (startGpuProbe,
// called first thing at boot: before the landing, the Map and the renderer
// load the GPU): a full-screen triangle at `px`² doing `taps` trilinear taps
// of a mipmapped 256² texture plus a little ALU per pixel — the shape of a
// terrain fragment. Timed by EXT_disjoint_timer_query_webgl2 where it exists
// (the GPU's own time for these draws, whatever else the GPU process does):
// TQ_N queries of TQ_K draws in one round, the median of the fast cluster
// per draw (timerVerdict). The wall clock times the start-up's queue, not the
// GPU (tier-probe: a 0.7 ms iGPU read 6–116 ms), so it is only the fallback
// where the extension is missing: every batch ends in a 1-pixel readback
// whose round trip (3–30 ms) swamps a 0.2 ms draw, so measureProbe times
// batches of K_LO and kHi draws, interleaved L H L … H L, and probeVerdict
// takes the median slope (hi − the mean of its two lo neighbours) / (kHi −
// K_LO): the round trip cancels, a stall in one pair is outvoted.

const K_LO = 1, K_MAX = 1024;
const WORK_MS = 12;          // kHi grows ×4 until a hi batch outlasts the lo by this
const PAIRS = 5, MIN_PAIRS = 3;
const RES_K = 8;             // the measured span must be ≥ this many clock ticks
const SPREAD = 0.5;          // the slopes' median absolute deviation / their median, at most
const TQ_N = 7, TQ_K = 4;     // the timer queries: TQ_N of TQ_K draws, ONE round
const TQ_CLUSTER = 1.5;      // the fast cluster: samples within this × the fastest

// The clock's resolution: the smallest step performance.now() takes (a
// clamped / farbled clock steps ≥ 1 ms; a spin of 20 ms that sees under two
// steps reports the spin's length).
export function clockRes(now) {
  const t0 = now();
  let last = t0, res = Infinity, steps = 0;
  while (steps < 4) {
    const t = now();
    if (t !== last) { res = Math.min(res, t - last); last = t; steps++; }
    if (t - t0 > 20) break;
  }
  return steps >= 2 ? res : Math.max(now() - t0, res === Infinity ? 0 : res);
}

// run(k): k draws + the readback, synchronous; now(): the clock. The budget
// maxMs is met once MIN_PAIRS pairs are in; 2 × maxMs is the hard cap.
// kHi's span target is WORK_MS or 2 × RES_K ticks of a coarse clock.
export function measureProbe(run, now, maxMs = 250) {
  const time = (k) => { const t = now(); run(k); return now() - t; };
  const res = clockRes(now);
  run(K_LO);                                  // compile + upload, not timed
  const t0 = now();
  const lo = [time(K_LO)], hi = [];
  let kHi = 4;
  for (;;) {                                  // calibrate: the last batch is hi[0]
    const h = time(kHi);
    if (h - lo[0] >= Math.max(WORK_MS, 2 * RES_K * res) || kHi >= K_MAX || now() - t0 > maxMs) { hi.push(h); break; }
    kHi = Math.min(K_MAX, kHi * 4);
  }
  lo.push(time(K_LO));
  while (hi.length < PAIRS) {
    const t = now() - t0;
    if (t > 2 * maxMs || (t > maxMs && hi.length >= MIN_PAIRS)) break;
    hi.push(time(kHi));
    lo.push(time(K_LO));
  }
  return { kLo: K_LO, kHi, lo, hi, res };
}

// ms per 1024² draw + whether it can be trusted: why 'slope' | 'clamped' (the
// median span is under RES_K clock ticks) | 'few' (under MIN_PAIRS pairs) |
// 'noisy' (the slopes disagree, or none is positive)
const q = (a, f) => a[Math.round(f * (a.length - 1))];
const mad = (a, med) => q(a.map((x) => Math.abs(x - med)).sort((x, y) => x - y), 0.5);
const per1024 = (x, px) => Math.round(x * (1024 * 1024) / (px * px) * 100) / 100;
export function probeVerdict(m, px = 512) {
  const span = m.hi.map((h, i) => h - (m.lo[i] + m.lo[i + 1]) / 2).sort((a, b) => a - b);
  const slope = span.map((d) => d / (m.kHi - m.kLo));
  const med = slope.length ? q(slope, 0.5) : NaN;
  const ms = Number.isFinite(med) ? per1024(med, px) : null;
  let why = 'slope';
  if (span.length && !(q(span, 0.5) >= RES_K * m.res)) why = 'clamped';
  else if (span.length < MIN_PAIRS) why = 'few';
  else if (!(med > 0) || mad(slope, med) / med > SPREAD) why = 'noisy';
  return { ms, ok: why === 'slope', why };
}

// The timer queries' ms per draw (null = lost: disjoint, never answered) →
// { ms per 1024², ok, why 'timer' | 'few' | 'noisy' }. Other work on the GPU
// only ever ADDS to a sample (boot: a 0.7 ms iGPU's median read 1.3–2.1 in
// the start-up), so the estimate is the FAST cluster — the samples within
// TQ_CLUSTER × the fastest — its median; fewer than MIN_PAIRS there = noisy.
export function timerVerdict(per, px = 512) {
  const v = per.filter((x) => Number.isFinite(x) && x > 0).sort((a, b) => a - b);
  const fast = v.filter((x) => x <= v[0] * TQ_CLUSTER);
  const med = fast.length ? q(fast, 0.5) : NaN;
  let why = 'timer';
  if (v.length < MIN_PAIRS) why = 'few';
  else if (fast.length < MIN_PAIRS) why = 'noisy';
  return { ms: Number.isFinite(med) ? per1024(med, px) : null, ok: why === 'timer', why };
}

const frame = () => new Promise((f) => setTimeout(f, 4));
// k draws per query, each its own query → ms per draw (null where lost)
async function timerRound(gl, tq, draw, k, n, waitMs) {
  const qs = [];
  for (let i = 0; i < n; i++) {
    const qq = gl.createQuery();
    gl.beginQuery(tq.TIME_ELAPSED_EXT, qq);
    draw(k);
    gl.endQuery(tq.TIME_ELAPSED_EXT);
    qs.push(qq);
  }
  gl.flush();
  const t0 = performance.now();
  while (!gl.getQueryParameter(qs[n - 1], gl.QUERY_RESULT_AVAILABLE) && performance.now() - t0 < waitMs) await frame();
  const lost = gl.getParameter(tq.GPU_DISJOINT_EXT);
  const out = qs.map((qq) => (!lost && gl.getQueryParameter(qq, gl.QUERY_RESULT_AVAILABLE)
    ? gl.getQueryParameter(qq, gl.QUERY_RESULT) / 1e6 / k : null));
  for (const qq of qs) gl.deleteQuery(qq);
  return out;
}

// One probe run: { ms, ok, why, gpu } (why: timerVerdict / probeVerdict's, or
// 'software' — CPU GL is never timed). Never throws.
// A 1×1 canvas (the pass draws into a px² texture); the context is lost
// the moment the run ends. A class that is never demoted (no demote_ms: the
// Quest) or CPU GL is not timed at all (cfg.any: time it anyway — tools).
export async function runGpuProbe(cfg = {}) {
  const px = cfg.px || 512, taps = cfg.taps || 16, maxMs = cfg.max_ms || 250;
  let gl = null;
  try {
    const c = typeof OffscreenCanvas !== 'undefined' ? new OffscreenCanvas(1, 1)
      : Object.assign(document.createElement('canvas'), { width: 1, height: 1 });
    gl = c.getContext('webgl2', { antialias: false, depth: false, stencil: false, alpha: false, powerPreference: 'high-performance' });
    if (!gl) return { ms: null, ok: false, why: 'failed' };
    const dbg = gl.getExtension('WEBGL_debug_renderer_info');
    const gpu = String(gl.getParameter(dbg ? dbg.UNMASKED_RENDERER_WEBGL : gl.RENDERER) || '');
    const cls = classOf(gpu);
    if (!cfg.any && cls === 'software') return { ms: null, ok: false, why: 'software', gpu };
    if (!cfg.any && (cfg.demote_ms || {})[cls] == null) return { ms: null, ok: false, why: 'no threshold', gpu };
    const sh = (type, src) => {
      const o = gl.createShader(type);
      gl.shaderSource(o, src);
      gl.compileShader(o);
      if (!gl.getShaderParameter(o, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(o));
      return o;
    };
    const pr = gl.createProgram();
    gl.attachShader(pr, sh(gl.VERTEX_SHADER, `#version 300 es
      in vec2 position; out vec2 vUv;
      void main() { vUv = position * 0.5 + 0.5; gl_Position = vec4(position, 0.0, 1.0); }`));
    gl.attachShader(pr, sh(gl.FRAGMENT_SHADER, `#version 300 es
      precision highp float; uniform sampler2D uTex; uniform float uSeed;
      in vec2 vUv; out vec4 o;
      void main() {
        vec4 acc = vec4(0.0); vec2 p = vUv * 7.3 + uSeed;
        for (int i = 0; i < ${taps}; i++) {
          float a = float(i) * 2.399;
          vec2 q = p * (1.0 + 0.37 * float(i)) + vec2(cos(a), sin(a)) * acc.xy;
          acc += texture(uTex, q);
        }
        o = acc / ${taps}.0;
      }`));
    gl.linkProgram(pr);
    if (!gl.getProgramParameter(pr, gl.LINK_STATUS)) throw new Error(gl.getProgramInfoLog(pr));
    gl.useProgram(pr);
    gl.bindBuffer(gl.ARRAY_BUFFER, gl.createBuffer());
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    const loc = gl.getAttribLocation(pr, 'position');
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
    const n = 256, data = new Uint8Array(n * n * 4);
    let s = 12345;
    for (let i = 0; i < data.length; i++) { s = (s * 1103515245 + 12345) >>> 0; data[i] = s >>> 24; }
    const tex = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, n, n, 0, gl.RGBA, gl.UNSIGNED_BYTE, data);
    gl.generateMipmap(gl.TEXTURE_2D);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.REPEAT);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.REPEAT);
    gl.uniform1i(gl.getUniformLocation(pr, 'uTex'), 0);
    const uSeed = gl.getUniformLocation(pr, 'uSeed');
    const rt = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, rt);
    gl.texStorage2D(gl.TEXTURE_2D, 1, gl.RGBA8, px, px);
    gl.bindFramebuffer(gl.FRAMEBUFFER, gl.createFramebuffer());
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, rt, 0);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.viewport(0, 0, px, px);
    gl.disable(gl.DEPTH_TEST);
    const one = new Uint8Array(4);
    const draw = (k) => { for (let i = 0; i < k; i++) { gl.uniform1f(uSeed, i * 0.013); gl.drawArrays(gl.TRIANGLES, 0, 3); } };
    const run = (k) => { draw(k); gl.readPixels(0, 0, 1, 1, gl.RGBA, gl.UNSIGNED_BYTE, one); };
    run(1);                                   // compile + upload, not timed
    const tq = gl.getExtension('EXT_disjoint_timer_query_webgl2');
    if (!tq) { await frame(); return { ...probeVerdict(measureProbe(run, () => performance.now(), maxMs), px), gpu }; }
    // ONE round (its answer comes a task or more later); a lost round (disjoint) is redone once
    let r = timerVerdict(await timerRound(gl, tq, draw, TQ_K, TQ_N, 4 * maxMs), px);
    if (r.why === 'few') r = timerVerdict(await timerRound(gl, tq, draw, TQ_K, TQ_N, 4 * maxMs), px);
    return { ...r, gpu };
  } catch (e) {
    console.warn('gpu probe failed:', e.message);
    return { ms: null, ok: false, why: 'failed' };
  } finally {
    const lose = gl && gl.getExtension('WEBGL_lose_context');
    if (lose) lose.loseContext();
  }
}

// THE boot probe: started once (main.js boot, first thing), read
// synchronously by detectDevice; boot never waits for it. It answers in
// ~0.3–0.9 s, often after the tier is decided, so a trusted result is kept in
// localStorage (STORE: renderer string + UA + the pass's px / taps) and a
// probe still running reads the last load's ('stored'); none → 'late'.
// A RETURN VISIT (owner 2026-10-27, M6c): a stored record whose UA | px | taps
// match (its renderer string is unknown before the renderer exists) — the boot
// does not run the probe; gpuProbe reads that record ('stored'; the full key,
// renderer string included, still checked — a miss is 'late', as today) and
// deferGpuProbe runs the probe ONCE after the first beam-in release, idle,
// never in XR, refreshing the store only (the tier is not touched mid-session).
const STORE = 'geovr_gpuprobe';
const keyTail = (cfg) => [typeof navigator !== 'undefined' ? navigator.userAgent : '', cfg.px || 512, cfg.taps || 16].join('|');
const storeKey = (gpu, cfg) => [gpu, keyTail(cfg)].join('|');
const storage = () => { try { return typeof localStorage !== 'undefined' ? localStorage : null; } catch { return null; } };
const readStore = () => { try { return JSON.parse((storage() && storage().getItem(STORE)) || 'null'); } catch { return null; } };
let _probeRun = null, _probe;
let _deferred = null;           // a return visit: { cfg, run, rec (the record boot read), armed }
// one run: logged; a trusted result stored
function probeRun(cfg, run, tag) {
  const t0 = performance.now();
  return run(cfg).then((r) => {
    console.info(`gpu probe${tag}: ${r.ms == null ? '—' : r.ms + ' ms'} (${r.why}) in ${Math.round(performance.now() - t0)} ms`);
    const st = storage();
    if (r.ok && st) try { st.setItem(STORE, JSON.stringify({ key: storeKey(r.gpu, cfg), ms: r.ms, why: r.why })); } catch { /* full / private */ }
    return r;
  });
}
// `run` (default runGpuProbe) is injectable for the gates
export function startGpuProbe(cfg = {}, run = runGpuProbe) {
  if (!_probeRun && !_deferred && cfg.enabled !== false) {
    const v = readStore();
    if (v && typeof v.key === 'string' && v.key.endsWith('|' + keyTail(cfg)) && Number.isFinite(v.ms)) {
      _deferred = { cfg, run, rec: v, armed: false };
      return null;
    }
    _probeRun = probeRun(cfg, run, '').then((r) => { _probe = r; return r; });
  }
  return _probeRun;
}
// the boot probe's result for the renderer (detectDevice's default probe)
export function gpuProbe(renderer, cfg = {}) {
  if (_probe) return _probe;
  if (!_probeRun && !_deferred) return { ms: null, ok: false, why: 'not started' };
  const v = _deferred ? _deferred.rec : readStore();
  if (v && v.key === storeKey(rendererString(renderer), cfg) && Number.isFinite(v.ms)) return { ms: v.ms, ok: true, why: 'stored' };
  return { ms: null, ok: false, why: 'late' };
}
// A return visit's deferred run (main.js: the beam-in overlay's release): once,
// at an idle point, never while renderer.xr presents (it waits for the
// session's end). The result only refreshes the store. `idle` is injectable
// for the gates. True when this call armed it.
const idleSoon = (fn) => (typeof requestIdleCallback === 'function' ? requestIdleCallback(fn) : setTimeout(fn, 0));
export function deferGpuProbe(renderer, idle = idleSoon) {
  const d = _deferred;
  if (!d || d.armed) return false;
  d.armed = true;
  const xr = renderer && renderer.xr;
  const go = () => {
    if (xr && xr.isPresenting) {
      const end = () => { xr.removeEventListener('sessionend', end); go(); };
      xr.addEventListener('sessionend', end);
      return;
    }
    idle(() => {
      if (xr && xr.isPresenting) { go(); return; }     // a session began meanwhile
      probeRun(d.cfg, d.run, ' (deferred)');
    });
  };
  go();
  return true;
}

// UNMASKED_RENDERER_WEBGL (or RENDERER) of a renderer's context, '' when hidden
function rendererString(renderer) {
  try {
    const gl = renderer.getContext();
    const ext = gl.getExtension('WEBGL_debug_renderer_info');
    return String(gl.getParameter(ext ? ext.UNMASKED_RENDERER_WEBGL : gl.RENDERER) || '');
  } catch { return ''; }
}

// the class of this page for a renderer string: the ?dev= pin, else UA + GPU
function classOf(gpu) {
  const nav = typeof navigator !== 'undefined' ? navigator : {};
  const touch = (nav.maxTouchPoints || 0) > 1 || (typeof window !== 'undefined' && 'ontouchstart' in window);
  return urlClass(typeof location !== 'undefined' ? location.search : '') || classifyDevice(gpu, nav.userAgent, touch);
}

// The boot decision. `ground` = renderconfig.ground; `pinned` = the tier a
// renderconfig.detail_quality pin or the caller forces (undefined = auto);
// `probe` (default: the boot probe's result) is injectable for the gates.
export function detectDevice(renderer, ground = {}, pinned, probe = gpuProbe) {
  const gpu = rendererString(renderer);
  const pin = urlClass(typeof location !== 'undefined' ? location.search : '');
  const cls = classOf(gpu);
  _cls = cls;
  const out = { cls, why_cls: pin ? 'url' : 'ua', gpu, probe_ms: null, probe_why: null, tier: 1, why: 'class', weak: false };
  const byClass = ground.tier_by_class || {};
  const clsTier = Number.isInteger(byClass[cls]) ? byClass[cls] : cls === 'software' ? 0 : cls.startsWith('desktop') ? 2 : 1;
  // the weak tier: the decided tier below the class's own (THE WEAK TIER)
  const done = () => { out.weak = _weak = cls !== 'quest' && out.tier < clsTier; return out; };
  const url = typeof location !== 'undefined' ? new URLSearchParams(location.search).get('ground') : null;
  if (url !== null && /^[012]$/.test(url)) { out.tier = +url; out.why = 'url'; return done(); }
  if (pinned !== undefined) { out.tier = pinned; out.why = 'pinned'; return done(); }
  out.tier = clsTier;
  const p = ground.probe || {};
  const lim = (p.demote_ms || {})[cls];
  if (cls !== 'software' && p.enabled !== false && lim != null) {
    // a bare number = a trusted measurement (the gates' injected probes)
    let r = probe(renderer, p);
    if (typeof r === 'number') r = { ms: r, ok: true, why: 'given' };
    out.probe_ms = r ? r.ms : null;
    out.probe_why = r ? r.why : 'failed';
    if (!r || !r.ok) out.why = 'unsure';          // no trusted time never demotes
    else if (out.probe_ms > lim && out.tier > 0) {
      out.tier -= 1;
      out.why = 'probe';
    }
  }
  return done();
}
